import { useEffect, useMemo, useState } from 'react';

import { authFetchWithExchangeRetry } from '../../lib/auth-fetch-with-exchange-retry.js';
import { isCancellation } from '../../lib/is-cancellation.js';
import { watchPageLeave } from '../../lib/page-leave.js';
import { reportConsultError } from '../../lib/report-consult-error.js';

import type { GroupConsultDayBucket, GroupConsultEvent } from './group-consult-calendar.js';
import {
  buildGroupConsultDayBuckets,
  getDefaultGroupConsultMonthState,
  getGroupConsultMonthKey,
  isRenderableGroupConsultEvent,
} from './group-consult-calendar.js';
import { TIMEZONE_OPTIONS } from './timezone-options.js';

export type GroupConsultViewMode = 'list' | 'month';

export type UseGroupConsultsParams = {
  /** Session JWT from the magic-link exchange; the webinar list only loads once it exists. */
  jwt: string | null;
  setJwt: (jwt: string) => void;
  /** Magic-link token, replayed by {@link authFetchWithExchangeRetry} when the JWT expires. */
  token: string | undefined;
  apiBase: string;
  /** Whether the group tab is offered at all (`consultType` gates the request, not `activeTab`). */
  enabled: boolean;
  activeTab: 'group' | '1:1';
};

export type GroupConsultsState = {
  events: GroupConsultEvent[];
  webinarsStatus: 'idle' | 'loading' | 'loaded' | 'error';
  webinarsError: string | null;
  displayTimeZone: string;
  setDisplayTimeZone: (timeZone: string) => void;
  groupViewMode: GroupConsultViewMode;
  setGroupViewMode: (viewMode: GroupConsultViewMode) => void;
  visibleGroupMonth: Date;
  setVisibleGroupMonth: (month: Date) => void;
  selectedGroupDayKey: string | null;
  setSelectedGroupDayKey: (dayKey: string | null) => void;
  groupDayBuckets: GroupConsultDayBucket[];
  selectedGroupDay: GroupConsultDayBucket | null;
};

/**
 * Owns the group-consult calendar: the webinar list request plus the view mode, display timezone,
 * visible month and selected day it is browsed with.
 *
 * @remarks Lifted verbatim out of the vendor page — same state, same effects, same order.
 */
export function useGroupConsults({
  jwt,
  setJwt,
  token,
  apiBase,
  enabled,
  activeTab,
}: UseGroupConsultsParams): GroupConsultsState {
  const [displayTimeZone, setDisplayTimeZone] = useState(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return TIMEZONE_OPTIONS.includes(tz) ? tz : 'America/New_York';
  }); // Defaults to user's local timezone when in TIMEZONE_OPTIONS
  const [events, setEvents] = useState<GroupConsultEvent[]>([]);
  const [groupViewMode, setGroupViewMode] = useState<GroupConsultViewMode>('list');
  const [visibleGroupMonth, setVisibleGroupMonth] = useState<Date>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedGroupDayKey, setSelectedGroupDayKey] = useState<string | null>(null);
  const [webinarsStatus, setWebinarsStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle',
  );
  const [webinarsError, setWebinarsError] = useState<string | null>(null);

  const groupDayBuckets = useMemo(
    () => buildGroupConsultDayBuckets(events, displayTimeZone),
    [events, displayTimeZone],
  );
  const selectedGroupDay = useMemo(
    () => groupDayBuckets.find((bucket) => bucket.dayKey === selectedGroupDayKey) ?? null,
    [groupDayBuckets, selectedGroupDayKey],
  );

  useEffect(() => {
    const { visibleMonth } = getDefaultGroupConsultMonthState(groupDayBuckets, displayTimeZone);
    setVisibleGroupMonth(visibleMonth);
    setSelectedGroupDayKey((currentSelectedDayKey) =>
      currentSelectedDayKey &&
      groupDayBuckets.some((bucket) => bucket.dayKey === currentSelectedDayKey)
        ? currentSelectedDayKey
        : null,
    );
  }, [displayTimeZone, groupDayBuckets]);

  useEffect(() => {
    const visibleMonthKey = getGroupConsultMonthKey(visibleGroupMonth);
    setSelectedGroupDayKey((currentSelectedDayKey) =>
      currentSelectedDayKey && currentSelectedDayKey.startsWith(visibleMonthKey)
        ? currentSelectedDayKey
        : null,
    );
  }, [visibleGroupMonth]);

  // The vendor's own Escape-to-close listener lived here (a `window` keydown guarded on
  // `activeTab === 'group' && groupViewMode === 'month' && selectedGroupDay`). Task 8 moved the day
  // slide-over onto `@nrx/ui`'s `Modal`, whose focus trap binds Escape on `document` for exactly
  // the lifetime of the open dialog — the same condition, since `GroupConsultDayPanel` renders
  // nowhere but inside that overlay. Keeping both meant two mechanisms implementing one behaviour,
  // with the surviving one uncovered. The listener is deleted rather than kept; the behaviour is
  // pinned by "closes on Escape" and the page-level Escape assertion in
  // group-consult-day-panel.spec.tsx.

  useEffect(() => {
    if (activeTab !== 'group' || groupViewMode !== 'month') {
      setSelectedGroupDayKey(null);
    }
  }, [activeTab, groupViewMode]);

  useEffect(() => {
    if (!jwt || !enabled) return;

    const controller = new AbortController();
    setWebinarsStatus('loading');
    setWebinarsError(null);

    const load = authFetchWithExchangeRetry({
      magicLinkToken: token,
      setJwt,
      apiBase,
      jwt,
      url: `${apiBase}/consult-scheduling/webinars`,
      init: { signal: controller.signal },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load sessions');
        // `unknown`, not `Promise<GroupConsultEvent[]>`: the cast was the only thing asserting the
        // element shape, and it asserted it of data nothing had checked.
        return res.json() as Promise<unknown>;
      })
      .then((data) => {
        // The array shape was checked here and the ELEMENT shape never was, so one session with a
        // non-ISO `start` reached `Intl.DateTimeFormat` inside the `groupDayBuckets` memo below —
        // during render — and unmounted the whole app. Dropping the bad element keeps the rest of
        // the calendar, and a response that is entirely malformed lands on the same empty state a
        // genuinely empty list does.
        setEvents(Array.isArray(data) ? data.filter(isRenderableGroupConsultEvent) : []);
        setWebinarsStatus('loaded');
      });
    const pageLeave = watchPageLeave(load);
    load.catch((err: unknown) => {
      // The signal, not the error name: Safari calls a cancelled load `TypeError: Load
      // failed`, which is indistinguishable from a real failure by wording alone.
      if (isCancellation(err, controller.signal)) return;
      // The member gets the generic line; the operator gets the real error. Before this, `err`
      // was discarded, so a parse failure, a CORS rejection and a genuine 500 were the same
      // string with nothing recorded anywhere.
      if (!pageLeave.leaving) reportConsultError('webinars-fetch', err);
      setWebinarsStatus('error');
      setWebinarsError('Unable to load sessions. Please try again later.');
    });

    return () => controller.abort();
    // Vendor dependency list, unchanged: `setJwt` and `apiBase` are stable for the page's lifetime,
    // and including `setJwt` would re-fetch the list on every render.
  }, [jwt, enabled, token]);

  return {
    events,
    webinarsStatus,
    webinarsError,
    displayTimeZone,
    setDisplayTimeZone,
    groupViewMode,
    setGroupViewMode,
    visibleGroupMonth,
    setVisibleGroupMonth,
    selectedGroupDayKey,
    setSelectedGroupDayKey,
    groupDayBuckets,
    selectedGroupDay,
  };
}
